import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { setupCache } from "axios-cache-interceptor";
import { Result } from "../types/Result";

export const JWT_TOKEN_KEY = "jwtToken";
export const REFRESH_TOKEN_KEY = "refreshToken";

export type UserToken = {
  token: string;
  refreshToken: string;
};

axios.interceptors.request.use((request) => {
  request.baseURL = process.env.REACT_APP_API_GATEWAY;
  return request;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);
export default axios;

const axiosApi = axios.create({ baseURL: process.env.REACT_APP_API_GATEWAY });

/** Interceptor'suz, sadece refresh-token çağrısı için kullanılan ham instance */
const bareAxios = axios.create({ baseURL: process.env.REACT_APP_API_GATEWAY });

export function clearStoredTokens() {
  localStorage.removeItem(JWT_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function storeTokens(tokens: UserToken) {
  localStorage.setItem(JWT_TOKEN_KEY, tokens.token);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
}

type AuthFailureHandler = (reason: Result<unknown> | string) => void;
let onAuthFailure: AuthFailureHandler | undefined;

/** Refresh token da geçersiz olduğunda (oturum bitti) çağrılacak handler'ı kaydeder. */
export function registerAuthFailureHandler(handler: AuthFailureHandler) {
  onAuthFailure = handler;
}

export class RefreshTokenError extends Error {
  constructor(public readonly result: Result<unknown> | string) {
    super(typeof result === "string" ? result : result.errors?.[0] ?? "");
    this.name = "RefreshTokenError";
  }
}

let refreshPromise: Promise<UserToken> | null = null;

/**
 * Access token'ı refresh token ile yeniler.
 * Backend her refresh'te yeni bir refresh token üretip eskisini geçersiz kıldığı
 * için aynı anda yalnızca TEK refresh isteği gönderilir (single-flight):
 * eş zamanlı tüm çağrılar aynı promise'i paylaşır.
 */
export function refreshTokens(): Promise<UserToken> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new RefreshTokenError("Refresh token bulunamadı");
    try {
      const response = await bareAxios.post<Result<UserToken>>(
        "api/Auth/refresh-token",
        { refreshToken }
      );
      const tokens = response.data.data;
      if (!response.data.isSuccess || !tokens?.token || !tokens.refreshToken)
        throw new RefreshTokenError(response.data);
      storeTokens(tokens);
      return tokens;
    } catch (error) {
      if (error instanceof RefreshTokenError) throw error;
      if (
        error instanceof AxiosError &&
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      )
        throw new RefreshTokenError(error.response.data as Result<unknown>);
      throw new RefreshTokenError("Oturum yenilenemedi");
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/** Refresh token'ın da geçersiz olduğu durum: depoyu temizle, uygulamayı haberdar et. */
export function handleAuthFailure(reason: Result<unknown> | string) {
  clearStoredTokens();
  onAuthFailure?.(reason);
}

axiosApi.interceptors.request.use((request) => {
  const token = localStorage.getItem(JWT_TOKEN_KEY);
  if (token) request.headers.Authorization = `Bearer ${token}`;
  else delete request.headers.Authorization;
  return request;
});

const axiosCacheApi = setupCache(axiosApi);

// 401 interceptor'ı setupCache'ten SONRA kaydedilir: axios-cache-interceptor'ın
// kendi hata işleyicisi önce çalışıp "loading" cache kaydını temizlemeli; aksi halde
// aynı anahtarla yapılan yeniden deneme hiç sonuçlanmayan cache beklemesine takılır.
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

const AUTH_ENDPOINTS = ["api/Auth/login", "api/Auth/refresh-token"];

axiosApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    const url = config?.url ?? "";
    const isAuthEndpoint = AUTH_ENDPOINTS.some((e) => url.includes(e));

    if (
      error.response?.status === 401 &&
      config &&
      !config._retried &&
      !isAuthEndpoint &&
      localStorage.getItem(REFRESH_TOKEN_KEY)
    ) {
      config._retried = true;
      try {
        const tokens = await refreshTokens();
        config.headers.Authorization = `Bearer ${tokens.token}`;
        return axiosApi(config);
      } catch (refreshError) {
        if (refreshError instanceof RefreshTokenError)
          handleAuthFailure(refreshError.result);
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);


export { axiosApi, axiosCacheApi };
